import PlacesAutocomplete from 'stimulus-places-autocomplete';
import { Loader } from '@googlemaps/js-api-loader';

export default class extends PlacesAutocomplete {
  static targets = super.targets.concat(['streetAddress', 'address2']);
  static values = { apiKey: String };

  // connect overridden to import the "places" library with @googlemaps/js-api-loader
  async connect() {
    if (!this.hasApiKeyValue) return;

    const loader = new Loader({ apiKey: this.apiKeyValue, version: 'weekly' });
    this.Places = await loader.importLibrary('places');
    this.initAutocomplete();
  }

  setAddressComponents(address) {
    const normalized = this.normalizeAddress();

    // Set normalized values to targets
    if (this.hasStreetAddressTarget)
      this.streetAddressTarget.value = normalized.address1;
    if (this.hasAddress2Target) this.address2Target.value = normalized.address2;
    if (this.hasCityTarget) this.cityTarget.value = normalized.suburb;
    if (this.hasStateTarget) this.stateTarget.value = normalized.state;
    if (this.hasPostalCodeTarget)
      this.postalCodeTarget.value = normalized.postcode;
    if (this.hasCountryTarget)
      this.countryTarget.value = normalized.country_code;
  }

  // set the Autocomplete options in this getter.
  get autocompleteOptions() {
    return {
      fields: ['address_components', 'formatted_address'],
      componentRestrictions: {
        country: this.countryValue,
      },
    };
  }

  // initAutocomplete is overridden so it uses the imported "places" library
  initAutocomplete() {
    if (!this.hasAddressTarget) return;

    this.autocomplete = new this.Places.Autocomplete(
      this.addressTarget,
      this.autocompleteOptions,
    );
    this.autocomplete.addListener('place_changed', this.placeChanged);
  }

  formatAddressComponents(addressComponents) {
    const data = {};

    addressComponents.forEach((component) => {
      const type = component.types[0];

      data[type] =
        type === 'country' ? component.short_name : component.long_name;
    });

    return data;
  }

  // Helper functions for address normalization
  getAddressComponent(type, short = false) {
    const c = this.place.address_components.find((x) => x.types.includes(type));
    return c ? (short ? c.short_name : c.long_name) : '';
  }

  join(...parts) {
    return parts.filter(Boolean).join(', ');
  }

  joinSpace(...parts) {
    return parts.filter(Boolean).join(' ');
  }

  normalizeAddress() {
    if (!this.place || !this.place.address_components) {
      return {
        address1: '',
        address2: '',
        suburb: '',
        postcode: '',
        state: '',
        country_code: '',
      };
    }

    // Extract all components
    const streetNumber = this.getAddressComponent('street_number');
    const route = this.getAddressComponent('route');
    const premise =
      this.getAddressComponent('premise') ||
      this.getAddressComponent('establishment');
    const subpremise = this.getAddressComponent('subpremise');
    const sl2 = this.getAddressComponent('sublocality_level_2');
    const sl1 =
      this.getAddressComponent('sublocality_level_1') ||
      this.getAddressComponent('sublocality');
    const neighborhood = this.getAddressComponent('neighborhood');
    const locality = this.getAddressComponent('locality');
    const postalTown = this.getAddressComponent('postal_town');
    const a3 = this.getAddressComponent('administrative_area_level_3');
    const a2 = this.getAddressComponent('administrative_area_level_2');
    const a1Short = this.getAddressComponent(
      'administrative_area_level_1',
      true,
    );
    const postalCode = this.getAddressComponent('postal_code');
    const countryShort = this.getAddressComponent('country', true);

    // address1 (priority)
    const streetLine = this.joinSpace(streetNumber, route);
    let address1 =
      streetLine ||
      premise ||
      sl2 ||
      neighborhood ||
      sl1 ||
      (this.place.formatted_address
        ? this.place.formatted_address.split(',')[0]
        : '');

    let address2;
    if (address1 && premise && address1 === premise) {
      address2 =
        [subpremise, sl2, sl1, neighborhood, premise].find(
          (c) => c && c !== address1,
        ) || '';
    } else {
      address2 = this.join(subpremise, premise);
    }

    // suburb / state / postcode / country
    const suburb = locality || postalTown || sl1 || a3 || a2 || '';
    const state =
      a1Short ||
      (a2 ? this.getAddressComponent('administrative_area_level_2', true) : '');
    const postcode = postalCode || '';
    const country_code = countryShort || '';

    return { address1, address2, suburb, postcode, state, country_code };
  }
}
